import React from 'react';

import { Box } from '@chakra-ui/react';
import { ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { CalibrationGridHoverTooltip } from 'uiToolkitV1/CalibrationGridHoverTooltip/CalibrationGridHoverTooltip';

const RenderDotAvatar = ({ cx, cy, value, imageUrl, renderPointsAsDots = false }) => {
  if (renderPointsAsDots) {
    return (
      <foreignObject x={cx - 4} y={cy - 4} width={8} height={8}>
        <Box width="8px" height="8px" bg="rgba(70, 87, 174, 0.8)" border="1px solid #fff" borderRadius="50%" />
      </foreignObject>
    );
  }

  return (
    <image
      x={cx - 10}
      y={cy - 10}
      width={20}
      height={20}
      xlinkHref={imageUrl}
      alt={value}
      clipPath="inset(0% round 50%)"
      preserveAspectRatio="xMidYMid slice"
    />
  );
};

function renderTooltip(data) {
  const { payload } = data;

  const tooltipData = {
    name: payload[0]?.payload?.value,
    email: calculateDisplayEmail(payload[0]?.payload?.employee),
    imageUrl: payload[0]?.payload?.imageUrl,
    ratings: [
      { name: 'Potential', value: payload?.[0]?.payload?.managerRating?.potential?.toFixed(2) },
      { name: 'Performance', value: payload?.[0]?.payload?.managerRating?.performance?.toFixed(2) },
      { name: 'Q&A', value: payload?.[0]?.payload?.managerRating?.qna?.toFixed(2) },
      { name: 'Overall', value: payload?.[0]?.payload?.managerRating?.overall?.toFixed(2) },
    ],
  };

  return <CalibrationGridHoverTooltip data={tooltipData} personHover showViewDetailsBtn={false} />;
}

function CalibrationScatterPlot({
  height,
  performanceGridEmpDetails,
  scoreRange,
  showTooltip,
  xAxisTicks,
  yAxisTicks,
  hideYAxisLabelTicks = false,
  renderPointsAsDots = false,
  hideScatterPoints = false,
}) {
  return (
    <Box position="absolute" zIndex={2} height="100%" width="100%">
      <ResponsiveContainer minHeight={height}>
        <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <Scatter
            name="Performance Grid"
            data={hideScatterPoints ? [] : performanceGridEmpDetails}
            shape={<RenderDotAvatar renderPointsAsDots={renderPointsAsDots} />}
          />
          <XAxis
            type="number"
            domain={
              hideYAxisLabelTicks
                ? [scoreRange.OVERALL.minScore, scoreRange.OVERALL.maxScore]
                : [scoreRange.PERFORMANCE.minScore, scoreRange.PERFORMANCE.maxScore]
            }
            dataKey={hideYAxisLabelTicks ? 'managerRating.overall' : 'managerRating.performance'}
            name={hideYAxisLabelTicks ? 'overall' : 'performance'}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#666666', fontSize: 10, fontWeight: 'bold' }}
            ticks={xAxisTicks}
            hide={false}
            height={40}
            label={{
              value: hideYAxisLabelTicks ? 'Overall →' : 'Performance (Goal score) →',
              position: 'insideBottom',
              fill: '#666666',
            }}
          />
          <YAxis
            type="number"
            domain={
              hideYAxisLabelTicks
                ? [0, 2] // when grid is 1d we want to center the avatars vertically, that why keeping domain from 0-2 and will override Y value to be 1 , to place vertically center
                : [scoreRange.POTENTIAL.minScore, scoreRange.POTENTIAL.maxScore]
            }
            dataKey={hideYAxisLabelTicks ? 'managerRating.dummyValueToCenterVertically' : 'managerRating.potential'}
            name="potential"
            axisLine={false}
            tickLine={false}
            tick={hideYAxisLabelTicks ? false : { fill: '#666666', fontSize: 10, fontWeight: 'bold' }}
            ticks={hideYAxisLabelTicks ? [] : yAxisTicks}
            hide={false}
            width={50}
            label={
              hideYAxisLabelTicks
                ? {}
                : {
                    value: hideYAxisLabelTicks ? '' : 'Potential (Competency score) →',
                    angle: -90,
                    position: 'insideLeft',
                    fill: '#666666',
                    style: { textAnchor: 'middle' },
                  }
            }
          />
          {showTooltip ? <Tooltip content={renderTooltip} /> : null}
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default CalibrationScatterPlot;
